import { Box, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import React, { useContext, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import OtherImage from "../../../../assets/AssetTypes/Other.svg";
import { ConfigContext } from "../../../../contexts/ConfigContext";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { ONBOARDING_FULFILLED_BY_TYPES } from "../../../shared/config";
import {
  DEFAULT_SLIDER_STEPS,
  LOAN_ASSET_TYPES,
  NO_NEMURU_INTERNATIONAL_STUDENT_SLIDER_STEPS,
  NO_NEMURU_SLIDER_STEPS,
  SLIDER_COLLECTIONS_BY_ON_BOARDING_TYPE,
  SLIDER_STEPS,
} from "../config";
import Slide1 from "./components/Slide1";
import Slide2 from "./components/Slide2";
import Slide3 from "./components/Slide3";
import Slide5 from "./components/Slide5";

import SlideController from "./components/SlideController";
import { LOCAL_STORAGE_KEY } from "../../../../config/config";
import { CustomRegistry } from "@nemuru-eng/js-api-client";
import { useActionCallback } from "../../../../utils/hooks/useActionCallback";

export default function Slider() {
  const {
    state: { loan, participant },
    dispatch,
  } = useContext(ProcessContext);
  const {
    state: { registry },
  } = useContext(ConfigContext);
  const isScreenXs = useMediaQuery(useTheme().breakpoints.down("xs"));
  const [state, setState] = React.useState({
    currentStep: 0,
    loanAssetTypeImage: null,
  });

  const { loanAssetTypeImage, loanAssetTypeText } = state;

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY.LOAN_ID, loan.id);

    const image =
      LOAN_ASSET_TYPES[loan.asset_reference[0].type]?.image || OtherImage;

    setState({
      ...state,
      loanAssetTypeImage: image,
    });
  }, []);

  useEffect(() => {
    const participantId = participant.participantId;
    if (!participantId) {
      dispatch({
        type: "REQUEST_PATCH_SET_BORROWER_ID_TO_BORROWER_ONBOARDING_DETAILS_BY_EMAIL_AND_PHONE",
        payload: {
          id: loan.id,
          onboarding_details_id: participant.onboardingDetails.id,
          phone_number: participant.onboardingDetails.invitation.phone_number,
          email: participant.onboardingDetails.invitation.email,
        },
      });
    }
  }, []);

  useActionCallback(
    "REQUEST_PATCH_SET_BORROWER_ID_TO_BORROWER_ONBOARDING_DETAILS_BY_EMAIL_AND_PHONE_FULFILLED",
    () => {
      dispatch({
        type: "REQUEST_GET_LOAN",
        payload: loan.id,
      });
    }
  );

  useActionCallback("REQUEST_GET_LOAN_FULFILLED", () => {
    if (participant.participantId) {
      dispatch({
        type: "REQUEST_POST_CREATE_BORROWER_OPTIN_AND_SEQUENCE",
        payload: {
          id: uuidv4(),
          loan_id: loan.id,
          borrower_id: participant.participantId,
        },
      });
    }
  });

  const OverlayImage = ({ currentStep, sliderCollection, ...props }) => {
    if (SLIDER_STEPS_COMPONENTS[sliderCollection[currentStep]].overlayImg) {
      return (
        <Box
          position="relative"
          display="flex"
          justifyContent={"center"}
          width="100%"
          marginBottom={"-1rem"}
          flex={10}
          left="0%"
          zIndex={1}
          style={{
            opacity: 0.8,
          }}
          {...props}
        >
          <img
            alt={`image for ${loanAssetTypeText}`}
            src={
              SLIDER_STEPS_COMPONENTS[sliderCollection[currentStep]].overlayImg
            }
            height={"100%"}
            style={{
              position: "absolute",
              margin: "0 auto",
              maxHeight: "100%",
              maxWidth: "100%",
              minHeight: "100px",
            }}
          />
        </Box>
      );
    }
    return null;
  };

  const onBoardingByBorrower =
    participant.onboardingFulfilledBy ===
      ONBOARDING_FULFILLED_BY_TYPES.BORROWER_POS ||
    participant.onboardingFulfilledBy ===
      ONBOARDING_FULFILLED_BY_TYPES.CLIENT_SIDE_KYC_SIGNATURE;

  const SLIDER_STEPS_COMPONENTS = {
    [SLIDER_STEPS.WELCOME_STEP]: {
      component: (
        <Slide1
          loan={loan}
          isScreenXs={isScreenXs}
          onBoardingByBorrower={onBoardingByBorrower}
        />
      ),
      overlayImg: loanAssetTypeImage,
    },
    [SLIDER_STEPS.WHAT_IS_NEMURU]: {
      component: <Slide2 isScreenXs={isScreenXs} />,
      overlayImg: OtherImage,
    },
    [SLIDER_STEPS.WHAT_WILL_YOU_NEED]: {
      component: (
        <Slide3
          isScreenXs={isScreenXs}
          onBoardingByBorrower={onBoardingByBorrower}
        />
      ),
      overlayImg: null,
    },
    [SLIDER_STEPS.WHAT_WILL_YOU_NEED_INTERNATIONAL_STUDENT]: {
      component: (
        <Slide5
          isScreenXs={isScreenXs}
          onBoardingByBorrower={onBoardingByBorrower}
        />
      ),
      overlayImg: null,
    },
  };

  const getSliderCollectionsByRegistry = (registry) => {
    switch (registry) {
      case CustomRegistry.CAIXABANK:
      case CustomRegistry.FACILITEA:
      case CustomRegistry.TESLA:
      case CustomRegistry.TECHNOLOGICAL_PLATFORM:
        return NO_NEMURU_SLIDER_STEPS;
      case CustomRegistry.UPF:
        return NO_NEMURU_INTERNATIONAL_STUDENT_SLIDER_STEPS;
      default:
        return DEFAULT_SLIDER_STEPS;
    }
  };

  const getSliderCollection = () => {
    const onBoardingFulfilledBy = participant.onboardingFulfilledBy;
    switch (onBoardingFulfilledBy) {
      case ONBOARDING_FULFILLED_BY_TYPES.BORROWER_POS:
      case ONBOARDING_FULFILLED_BY_TYPES.CLIENT_SIDE_KYC_SIGNATURE:
        return getSliderCollectionsByRegistry(registry);
      case ONBOARDING_FULFILLED_BY_TYPES.MERCHANT_POS:
        return SLIDER_COLLECTIONS_BY_ON_BOARDING_TYPE[
          ONBOARDING_FULFILLED_BY_TYPES.MERCHANT_POS
        ];
      case ONBOARDING_FULFILLED_BY_TYPES.BORROWER_ONLINE:
        return SLIDER_COLLECTIONS_BY_ON_BOARDING_TYPE[
          ONBOARDING_FULFILLED_BY_TYPES.BORROWER_ONLINE
        ];
      default:
        return getSliderCollectionsByRegistry(registry);
    }
  };

  const sliderCollection = getSliderCollection();

  const goToNextStep = () =>
    setState((s) => ({ ...s, currentStep: s.currentStep + 1 }));
  const goToPrevStep = () =>
    setState((s) => ({ ...s, currentStep: s.currentStep - 1 }));

  const { currentStep } = state;

  return (
    <>
      <Box px={2} flex={1} display={"flex"}>
        {/*<Box height="5vh" />*/}
        {SLIDER_STEPS_COMPONENTS[sliderCollection[currentStep]].component}
      </Box>
      <OverlayImage
        currentStep={currentStep}
        sliderCollection={sliderCollection}
      />
      <SlideController
        currentStep={currentStep}
        goToNextStep={goToNextStep}
        goToPrevStep={goToPrevStep}
        sliderCollection={sliderCollection}
        sliderStepsComponents={SLIDER_STEPS_COMPONENTS}
      />
    </>
  );
}
